import { useEditor } from "@/utils/useEditor"
import { Slider } from "../ui/Slider"
import { MouseEvent, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { getParametricCurvePoint, getParametricCurvesData } from "@/core/parametric-curves"
import { clamp } from "@/utils/math"
import { Adjustments } from "@/core/types"
import { getDefaultAdjustments } from "@/core/adjustments"

const parametricKnobWidth = 8

const parametricSettings: { [key in keyof Partial<Adjustments>]: string } = {
	parametricShadows: "parametricCurves.sliderShadows",
	parametricDarks: "parametricCurves.sliderDarks",
	parametricLights: "parametricCurves.sliderLights",
	parametricHighlights: "parametricCurves.sliderHighlights",
}

// Stable English names for history messages (never translated)
const parametricHistoryNames: Record<string, string> = {
	parametricShadows: "Shadows",
	parametricDarks: "Darks",
	parametricLights: "Lights",
	parametricHighlights: "Highlights",
}

const parametricStopsSettings: { [key in keyof Partial<Adjustments>]: string } = {
	parametricDarksStop: "parametricCurves.stopDarks",
	parametricMidsStop: "parametricCurves.stopMids",
	parametricLightsStop: "parametricCurves.stopLights",
}

export const extractParametricAdjustments = (adjustments: Adjustments) => {
	return {
		parametricHighlights: adjustments.parametricHighlights,
		parametricLights: adjustments.parametricLights,
		parametricDarks: adjustments.parametricDarks,
		parametricShadows: adjustments.parametricShadows,
		parametricDarksStop: adjustments.parametricDarksStop,
		parametricLightsStop: adjustments.parametricLightsStop,
		parametricMidsStop: adjustments.parametricMidsStop,
	}
}

export const ParametricCurves = (props: {
	values: Adjustments
	saveChange: (name: string, val?: number | string) => void
	isLightMode: boolean
}) => {
	const { t } = useTranslation("components")
	const editor = useEditor()
	const canvasElement = useRef<HTMLCanvasElement>(null)
	const [visibleAreaName, setVisibleAreaName] = useState<string | null>(null)
	const [draggedStopName, setDraggedStopName] = useState<string | null>(null)
	const [draggedAreaName, setDraggedAreaName] = useState<string | null>(null)
	const [hoveringStopName, setHoveringStopName] = useState<string | null>(null)
	const [hoveringGridPoint, setHoveringGridPoint] = useState<number[] | null>(null)
	const isLightMode = props.isLightMode === true

	const [localState, setLocalState] = useState(() => extractParametricAdjustments(props.values))

	useEffect(() => {
		setLocalState(extractParametricAdjustments(props.values))
	}, [props.values])

	useEffect(() => {
		draw()
	}, [localState, visibleAreaName, hoveringStopName, hoveringGridPoint, isLightMode])

	/* Actions */

	const handleGridReset = (event: MouseEvent<HTMLCanvasElement>) => {
		if (event.detail !== 2) {
			return
		}

		event.preventDefault()

		if (visibleAreaName === null) {
			return
		}

		const defaultAdjustments = getDefaultAdjustments()

		handleUpdate(visibleAreaName, defaultAdjustments[visibleAreaName])
		handleUpdateEnded(`Reset Parametric ${parametricHistoryNames[visibleAreaName]}`)
	}

	const handleGridDrag = (event: React.PointerEvent<HTMLCanvasElement>) => {
		const target = event.target as HTMLDivElement
		const targetRect = target.getBoundingClientRect()

		const x = event.clientX - targetRect.left
		const y = event.clientY - targetRect.top

		const point = [clamp(x / targetRect.width, 0, 1), clamp(y / targetRect.height, 0, 1)]

		let areaName = "parametricHighlights"

		Object.keys(parametricStopsSettings).every((name, index) => {
			const value = localState[name]

			if (point[0] && point[0] <= value) {
				areaName = Object.keys(parametricSettings)[index] ?? areaName
				return false
			}

			return true
		})

		if (event.type === "pointerdown") {
			setDraggedAreaName(areaName)
			target.setPointerCapture(event.pointerId)
			target.requestPointerLock()
		} else if (event.type === "pointermove") {
			setHoveringGridPoint(point)
			setVisibleAreaName(areaName)

			if (draggedAreaName !== null) {
				const offsetY = -event.nativeEvent.movementY / 30
				const newValue = clamp(localState[areaName] + offsetY, -1, 1)

				handleUpdate(areaName, newValue)
			}
		} else if (event.type === "pointerleave") {
			setHoveringGridPoint(null)
			setVisibleAreaName(null)
		} else {
			target.releasePointerCapture(event.pointerId)
			document.exitPointerLock()

			handleUpdateEnded(
				`Parametric ${parametricHistoryNames[areaName]}`,
				Math.round(localState[areaName] * 100),
			)

			setDraggedAreaName(null)
		}
	}

	const handleStopReset = (event: React.PointerEvent<HTMLDivElement>) => {
		const target = event.target as HTMLDivElement
		const name = target.getAttribute("data-name") as keyof Adjustments
		const defaultAdjustments = getDefaultAdjustments()

		setLocalState({
			...localState,
			[name]: defaultAdjustments[name],
		})
	}

	const handleStopDrag = (event: React.PointerEvent<HTMLDivElement>) => {
		const target = event.target as HTMLDivElement
		const slider = target.parentElement?.parentElement as HTMLDivElement
		const name = target.getAttribute("data-name")

		const sliderRect = slider.getBoundingClientRect()

		const allSettings = Object.keys(parametricStopsSettings)

		if (event.type === "pointerdown") {
			setDraggedStopName(name)
			target.setPointerCapture(event.pointerId)
		} else if (event.type === "pointermove") {
			if (draggedStopName === null) {
				return
			}

			let newValue = (event.clientX - sliderRect.left) / slider.clientWidth

			const currentIndex = allSettings.indexOf(draggedStopName)

			const minValue =
				currentIndex === 0 ? 0 : localState[allSettings[currentIndex - 1] as string] + 0.05
			const maxValue =
				currentIndex === allSettings.length - 1
					? 1
					: localState[allSettings[currentIndex + 1] as string] - 0.05

			newValue = clamp(newValue, minValue, maxValue)

			editor.setAdjustments({ [draggedStopName]: newValue })

			setLocalState({
				...localState,
				[draggedStopName]: newValue,
			})
		} else {
			setDraggedStopName(null)
			target.releasePointerCapture(event.pointerId)
			draw()
		}
	}

	const getCurve = (state): [number[], number[]] => {
		const curve = getParametricCurvesData(state)

		const x = Array.from({ length: curve.length }, (_, i) => i).map(
			(x) => x / (curve.length - 1),
		)
		const y: number[] = Array.prototype.slice.call(curve)

		return [x, y]
	}

	const drawGrid = (ctx: CanvasRenderingContext2D, w, h) => {
		const xspacing = w / 4
		const yspacing = h / 4

		ctx.save()

		ctx.strokeStyle = isLightMode ? "rgba(0, 0, 0, 0.2)" : "#333"
		ctx.lineWidth = 1

		for (let x = 0; x <= w; x += xspacing) {
			ctx.beginPath()
			ctx.moveTo(x, 0)
			ctx.lineTo(x, h)
			ctx.stroke()
			ctx.closePath()
		}

		for (let y = 0; y <= h; y += yspacing) {
			ctx.beginPath()
			ctx.moveTo(0, y)
			ctx.lineTo(w, y)
			ctx.stroke()
			ctx.closePath()
		}

		if (hoveringStopName != null) {
			ctx.strokeStyle = isLightMode ? "rgba(0, 0, 0, 0.25)" : "#ffffff33"
			ctx.lineWidth = 2

			const value = localState[hoveringStopName]

			const x = parametricKnobWidth / 2 + value * (w - parametricKnobWidth)

			ctx.beginPath()
			ctx.moveTo(x, 0)
			ctx.lineTo(x, h)
			ctx.stroke()
			ctx.closePath()
		}

		ctx.restore()
	}

	const drawIdentity = (ctx: CanvasRenderingContext2D, w, h) => {
		ctx.save()
		ctx.strokeStyle = isLightMode ? "rgba(0, 0, 0, 0.4)" : "#444"
		ctx.lineWidth = 2

		ctx.beginPath()
		ctx.moveTo(0, h)
		ctx.lineTo(w, 0)
		ctx.stroke()

		ctx.restore()
	}

	const drawCurve = (ctx: CanvasRenderingContext2D, w, h, curve) => {
		const [x, y] = curve

		ctx.strokeStyle = isLightMode ? "#222" : "#fff"
		ctx.lineWidth = 2

		ctx.beginPath()

		for (let i = 0; i < x.length; i++) {
			const rx = x[i] * w
			const ry = h - y[i] * h

			if (i == 0) {
				ctx.moveTo(rx, ry)
				continue
			}

			ctx.lineTo(rx, ry)
		}

		ctx.stroke()
	}

	const drawCurvePoint = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
		if (hoveringGridPoint === null) {
			return
		}

		const point = hoveringGridPoint[0] || 0
		const y = 1.0 - getParametricCurvePoint(point)

		ctx.save()

		ctx.strokeStyle = isLightMode ? "#222" : "#fff"
		ctx.fillStyle = isLightMode ? "#fff" : "#000"
		ctx.lineWidth = 1

		ctx.beginPath()
		ctx.arc(point * w, y * h, 4, 0, 2 * Math.PI)
		ctx.closePath()

		ctx.fill()
		ctx.stroke()

		ctx.restore()
	}

	const drawArea = (
		ctx: CanvasRenderingContext2D,
		w: number,
		h: number,
		lbound: [number[], number[]],
		ubound: [number[], number[]],
	) => {
		const [x, y] = lbound

		ctx.fillStyle = isLightMode ? "rgba(0, 0, 0, 0.06)" : "rgba(255, 255, 255, 0.06)"

		const region = new Path2D()

		for (let i = 0; i < x.length; i++) {
			const rx = (x[i] as number) * w
			const ry = h - (y[i] as number) * h

			if (i == 0) {
				region.moveTo(rx, ry)
				continue
			}

			region.lineTo(rx, ry)
		}

		const [ux, uy] = ubound

		for (let i = ux.length; i > 0; i--) {
			const rx = (ux[i] as number) * w
			const ry = h - (uy[i] as number) * h

			if (i == 0) {
				region.lineTo(rx, ry)
				continue
			}

			region.lineTo(rx, ry)
		}

		region.closePath()

		ctx.fill(region)
	}

	const draw = () => {
		const element = canvasElement.current

		if (!element) {
			return
		}

		const ctx = element.getContext("2d")

		const screenScale = window.devicePixelRatio || 1
		const w = element.offsetWidth
		const h = element.offsetHeight

		if (!ctx || !w || !h) {
			return
		}

		element.width = w * screenScale
		element.height = h * screenScale

		ctx.scale(screenScale, screenScale)
		ctx.clearRect(0, 0, w, h)

		drawGrid(ctx, w, h)

		// Draging areas when hovering a slider
		if (visibleAreaName !== null) {
			drawArea(
				ctx,
				w,
				h,
				getCurve({ ...localState, [visibleAreaName]: -1 }),
				getCurve({ ...localState, [visibleAreaName]: 1 }),
			)
		}

		drawIdentity(ctx, w, h)
		drawCurve(ctx, w, h, getCurve(localState))

		if (hoveringGridPoint != null) {
			drawCurvePoint(ctx, w, h)
		}
	}

	const handleUpdate = (name: string, value: number) => {
		editor.setAdjustments({ [name]: value })
		setLocalState({ ...localState, [name]: value } as Adjustments)
	}

	const handleUpdateEnded = (name: string, value?: number | string) => {
		props.saveChange(name, value)
	}

	/* Render */

	return (
		<div className="relative mt-2 box-border flex w-full shrink-0 select-none flex-col">
			<canvas
				ref={canvasElement}
				className="aspect-square w-full p-0.5"
				onPointerDown={handleGridDrag}
				onPointerMove={handleGridDrag}
				onPointerUp={handleGridDrag}
				onPointerLeave={handleGridDrag}
				onMouseUp={handleGridReset}
			/>
			<div className="pointer-events-none absolute left-0 top-0 aspect-square w-full">
				{hoveringGridPoint && hoveringGridPoint[0] != null && (
					<span className={`absolute left-0 top-0 text-xs text-base-400`}>
						{(hoveringGridPoint[0] * 255).toFixed(0)} /&nbsp;
						{(getParametricCurvePoint(hoveringGridPoint[0]) * 255).toFixed(0)}
					</span>
				)}
				{visibleAreaName && (
					<span
						className={`absolute left-[50%] translate-x-[-50%] text-xs text-base-400`}
					>
						{t(parametricSettings[visibleAreaName] ?? "")}
					</span>
				)}
			</div>

			<div className="relative mt-2 box-border h-4 w-full rounded-sm border border-base-600 bg-gradient-to-r from-base-900 to-base-500 dark:h-3 dark:border-0 dark:border-black">
				<div className={`absolute left-0 right-[10px] top-1/2 h-3 -translate-y-1/2`}>
					{Object.entries(parametricStopsSettings).map(([key], index) => {
						let offset = "right-[110%]"

						if (index !== Object.keys(parametricStopsSettings).length - 1) {
							offset = "left-[110%]"
						}

						return (
							<div
								key={key}
								onPointerEnter={() => setHoveringStopName(key)}
								onPointerLeave={() => setHoveringStopName(null)}
								onPointerDown={handleStopDrag}
								onPointerMove={handleStopDrag}
								onPointerUp={handleStopDrag}
								onDoubleClick={handleStopReset}
								style={{ left: localState[key] * 100 + "%" }}
								data-name={key}
								className={`group absolute size-[12px] rounded-full border-2 border-solid border-base-400 bg-base-800 hover:border-base-100 active:border-4`}
							>
								<span
									className={`pointer-events-none absolute top-[-32px] text-xs ${offset} hidden group-hover:block`}
								>
									{Math.round(localState[key] * 100)}
								</span>
							</div>
						)
					})}
				</div>
			</div>
			<div className="relative mt-3 w-full">
				{Object.entries(parametricSettings).map(([key, name]) => {
					return (
						<Slider
							key={key}
							title={t(name)}
							panelName={t("parametricCurves.panelCurves")}
							focusKey={key}
							keyboardStep={1}
							shiftKeyboardStep={10}
							hoverKeyboardStep={5}
							hoverShiftKeyboardStep={20}
							value={props.values[key]}
							onMouseEnter={(_) => setVisibleAreaName(key)}
							onMouseLeave={(_) => setVisibleAreaName(null)}
							onUpdate={(val) => handleUpdate(key, val)}
							onUpdateEnded={(_, displayedValue) => {
								handleUpdateEnded(
									`Parametric ${parametricHistoryNames[key]}`,
									displayedValue,
								)
							}}
						/>
					)
				})}
			</div>
		</div>
	)
}
